import React, { useEffect, useState } from "react";
import axios from "axios";
import DashboardLayout from "./DashboardLayout";
import { ADMIN_SESSION_EVENT, getSelectedAdminSession, sessionParam } from "../utils/adminSession";

const API = "/backend/public/api";
const uploadsBase = "/uploads";

const overlayStyle = { position: "fixed", inset: 0, background: "rgba(15,23,42,0.58)", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", zIndex: 3000 };
const modalStyle = { background: "#fff", width: "min(1100px, 100%)", maxHeight: "92vh", overflow: "auto", borderRadius: "22px", padding: "24px", boxShadow: "0 30px 70px rgba(15,23,42,0.28)" };
const detailGridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "14px" };
const tableShellStyle = { overflowX: "auto", overflowY: "visible", background: "#fff", boxShadow: "0 .125rem .25rem rgba(0,0,0,.075)", borderRadius: "1rem", padding: "0.5rem", position: "relative", zIndex: 1 };
const marksModalStyle = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: "20px", boxShadow: "0 24px 50px rgba(15,23,42,0.24)", padding: "18px", display: "grid", gap: "12px", width: "min(760px, 100%)", maxHeight: "88vh", overflow: "auto" };
const toastStyle = { position: "fixed", top: "84px", right: "20px", minWidth: "280px", maxWidth: "360px", padding: "14px 16px", borderRadius: "16px", boxShadow: "0 18px 40px rgba(15,23,42,0.18)", zIndex: 6000, display: "grid", gap: "4px" };

function fileUrl(name) {
  return name ? `${uploadsBase}/${name}` : "";
}

function StatusChip({ status }) {
  const current = status || "pending";
  const styles = {
    approved: { color: "#166534", background: "#dcfce7" },
    hold: { color: "#92400e", background: "#fef3c7" },
    rejected: { color: "#b91c1c", background: "#fee2e2" },
    pending: { color: "#1d4ed8", background: "#dbeafe" },
  };

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "6px 10px",
        borderRadius: "999px",
        fontSize: "12px",
        fontWeight: 700,
        textTransform: "capitalize",
        ...(styles[current] || styles.pending),
      }}
    >
      {current}
    </span>
  );
}

function DetailTile({ label, value }) {
  return (
    <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "16px", padding: "14px" }}>
      <div style={{ color: "#64748b", fontSize: "12px", marginBottom: "6px", fontWeight: 700 }}>{label}</div>
      <div style={{ color: "#0f172a", fontWeight: 600, wordBreak: "break-word" }}>{value || "-"}</div>
    </div>
  );
}

function DocumentCard({ label, fileName, isImage }) {
  return (
    <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "16px", padding: "14px", textAlign: "center" }}>
      <div style={{ color: "#475569", fontWeight: 700, marginBottom: "10px" }}>{label}</div>
      {fileName ? (
        isImage ? (
          <img src={fileUrl(fileName)} alt={label} style={{ width: "100%", maxHeight: "220px", objectFit: "contain", borderRadius: "12px" }} />
        ) : (
          <a href={fileUrl(fileName)} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm">
            Open {label}
          </a>
        )
      ) : (
        <div style={{ color: "#94a3b8" }}>Not uploaded</div>
      )}
    </div>
  );
}

function ExamCompletedStudents() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showView, setShowView] = useState(false);
  const [marksPopup, setMarksPopup] = useState(null);
  const [marksData, setMarksData] = useState(null);
  const [marksLoading, setMarksLoading] = useState(false);
  const [marksSaving, setMarksSaving] = useState(false);
  const [adminRemark, setAdminRemark] = useState("");
  const [toast, setToast] = useState(null);

  const loadRows = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/student-applications`, {
        params: { workflow_stage: "exam_completed", session_year: sessionParam(), _ts: Date.now() },
      });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
    const reload = () => loadRows();
    window.addEventListener(ADMIN_SESSION_EVENT, reload);
    return () => window.removeEventListener(ADMIN_SESSION_EVENT, reload);
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(timer);
  }, [toast]);

  const showToast = (type, title, message) => {
    setToast({ type, title, message });
  };

  const openView = async (id) => {
    try {
      const res = await axios.get(`${API}/student-applications/${id}`);
      setSelected(res.data);
      setShowView(true);
    } catch (error) {
      showToast("error", "Load Failed", error.response?.data?.message || "Student data load failed");
    }
  };

  const openMarksPopup = async (row) => {
    setMarksPopup({ row });
    setAdminRemark("");
    setMarksData(null);

    try {
      setMarksLoading(true);
      const res = await axios.get(`${API}/student-applications/${row.id}/marks`);
      setMarksData(res.data);
    } catch (error) {
      showToast("error", "Marks Load Failed", error.response?.data?.message || "Marks data load failed");
    } finally {
      setMarksLoading(false);
    }
  };

  const updateMarksRow = (moduleId, key, value) => {
    setMarksData((prev) => ({
      ...prev,
      rows: (prev?.rows || []).map((item) => (item.module_id === moduleId ? { ...item, [key]: value } : item)),
    }));
  };

  const submitAction = async (action) => {
    if (!marksPopup?.row) return;
    if ((action === "reject" || action === "edit") && !adminRemark.trim()) {
      showToast("error", "Remark Required", "Edit ba reject korar jonno remark likhte hobe.");
      return;
    }

    try {
      setMarksSaving(true);
      await axios.post(`${API}/student-applications/${marksPopup.row.id}/marks/admin-action`, {
        action,
        rows: marksData?.rows || [],
        admin_remark: adminRemark.trim(),
      });
      setMarksPopup(null);
      setMarksData(null);
      setAdminRemark("");
      await loadRows();
      showToast("success", "Approval Updated", `Marks workflow moved with ${action} action.`);
    } catch (error) {
      showToast("error", "Action Failed", error.response?.data?.message || "Marks approval action failed");
    } finally {
      setMarksSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="container-fluid">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div>
            <h3 className="mb-1">Exam Complete</h3>
            <p className="text-muted mb-0">Session: {getSelectedAdminSession()} | Franchise-submitted exam completed students wait here for Head of Institute marks approval.</p>
          </div>
          <button className="btn btn-dark" onClick={loadRows} disabled={loading}>
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>

        <div style={tableShellStyle}>
          <table className="table align-middle mb-0">
            <thead>
              <tr>
                <th>Sl No</th>
                <th>Registration No</th>
                <th>Student</th>
                <th>Course</th>
                <th>Study Centre</th>
                <th>Status</th>
                <th>Marks Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.map((row, index) => (
                <tr key={row.id}>
                  <td>{index + 1}</td>
                  <td>{row.registration_no || "-"}</td>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      <img
                        src={row.photo ? fileUrl(row.photo) : "https://placehold.co/52x52/e2e8f0/475569?text=ST"}
                        alt={row.student_name}
                        style={{ width: "52px", height: "52px", borderRadius: "14px", objectFit: "cover", border: "1px solid #e2e8f0" }}
                      />
                      <div>
                        <strong>{row.student_name}</strong>
                        <div className="text-muted small">{row.application_no || `APP-${row.id}`}</div>
                      </div>
                    </div>
                  </td>
                  <td>{row.course_name || "-"}</td>
                  <td>{row.study_centre_name || row.study_centre_login || "-"}</td>
                  <td><StatusChip status={row.status} /></td>
                  <td>{row.workflow_stage_remark || "Approval Pending from HO"}</td>
                  <td>
                    <div className="d-flex flex-wrap gap-2 position-relative">
                      <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openView(row.id)}>View</button>
                      <button type="button" className="btn btn-sm btn-outline-dark" onClick={() => openMarksPopup(row)}>Marks Action</button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center py-4 text-muted">
                    No exam completed student found.
                  </td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan="8" className="text-center py-4 text-muted">
                    Loading exam completed students...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {marksPopup ? (
        <div style={overlayStyle} onClick={() => setMarksPopup(null)}>
          <div style={marksModalStyle} onClick={(event) => event.stopPropagation()}>
            <div className="d-flex flex-wrap justify-content-between gap-2 align-items-start">
              <div>
                <h4 className="mb-1" style={{ color: "#0f172a" }}>Marks Entry Approval</h4>
                <div style={{ color: "#64748b", fontSize: "14px" }}>{marksPopup.row.student_name}</div>
              </div>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setMarksPopup(null)} disabled={marksSaving}>
                Close
              </button>
            </div>
            {marksLoading ? (
              <div className="text-muted small">Loading marks...</div>
            ) : (
              <>
                <div style={{ maxHeight: "320px", overflow: "auto", border: "1px solid #e2e8f0", borderRadius: "14px", padding: "10px" }}>
                  {(marksData?.rows || []).length ? marksData.rows.map((row) => (
                    <div key={row.module_id} style={{ display: "grid", gridTemplateColumns: "1.5fr repeat(4, minmax(120px, 1fr))", gap: "10px", alignItems: "start", marginBottom: "10px" }}>
                      <div>
                        <div style={{ fontSize: "11px", color: "#64748b", fontWeight: 700 }}>{row.semester_title || `Semester ${row.semester_no || 1}`}</div>
                        <div style={{ fontSize: "13px", color: "#334155", fontWeight: 700 }}>{row.module_title}</div>
                      </div>
                      {[
                        ["Theory", "marks_obtained", "max_marks", "pass_marks"],
                        ["Practical", "practical_marks_obtained", "practical_max_marks", "practical_pass_marks"],
                        ["Project", "project_marks_obtained", "project_max_marks", "project_pass_marks"],
                        ["Viva", "viva_marks_obtained", "viva_max_marks", "viva_pass_marks"],
                      ].map(([label, obtainedKey, fullKey, passKey]) => (
                        <div key={label}>
                          <div style={{ fontSize: "11px", fontWeight: 800, color: "#64748b", marginBottom: "4px" }}>{label}</div>
                          <input className="form-control form-control-sm mb-1" type="number" placeholder="Obtained" value={row[obtainedKey] ?? ""} onChange={(event) => updateMarksRow(row.module_id, obtainedKey, event.target.value)} />
                          <input className="form-control form-control-sm mb-1" type="number" placeholder="Full" value={row[fullKey] ?? ""} onChange={(event) => updateMarksRow(row.module_id, fullKey, event.target.value)} />
                          <input className="form-control form-control-sm" type="number" placeholder="Pass" value={row[passKey] ?? ""} onChange={(event) => updateMarksRow(row.module_id, passKey, event.target.value)} />
                        </div>
                      ))}
                    </div>
                  )) : <div className="text-muted small">No marks rows available.</div>}
                </div>
                <textarea
                  className="form-control"
                  rows="3"
                  value={adminRemark}
                  onChange={(event) => setAdminRemark(event.target.value)}
                  placeholder="Edit or reject korle remark likhun"
                ></textarea>
                <div className="d-flex flex-wrap gap-2">
                  <button type="button" className="btn btn-sm btn-success" onClick={() => submitAction("approve")} disabled={marksSaving}>Approve</button>
                  <button type="button" className="btn btn-sm btn-warning" onClick={() => submitAction("edit")} disabled={marksSaving}>Edit</button>
                  <button type="button" className="btn btn-sm btn-danger" onClick={() => submitAction("reject")} disabled={marksSaving}>Reject</button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}

      {toast ? (
        <div
          style={{
            ...toastStyle,
            background: toast.type === "success" ? "#ecfdf5" : "#fef2f2",
            border: `1px solid ${toast.type === "success" ? "#a7f3d0" : "#fecaca"}`,
            color: toast.type === "success" ? "#065f46" : "#991b1b",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: 800 }}>
            <i className={`bi ${toast.type === "success" ? "bi-check2-circle" : "bi-exclamation-octagon"}`}></i>
            {toast.title}
          </div>
          <div style={{ fontSize: "13px", lineHeight: 1.5 }}>{toast.message}</div>
        </div>
      ) : null}

      {showView && selected ? (
        <div style={overlayStyle} onClick={() => setShowView(false)}>
          <div style={modalStyle} onClick={(event) => event.stopPropagation()}>
            <div className="d-flex flex-wrap justify-content-between gap-2 mb-3">
              <div>
                <h3 className="mb-1">Exam Completed Student View</h3>
                <p className="text-muted mb-0">Full student details with image and uploaded documents.</p>
              </div>
              <button type="button" className="btn btn-outline-secondary" onClick={() => setShowView(false)}>
                Close
              </button>
            </div>

            <div style={detailGridStyle}>
              <DetailTile label="Application No" value={selected.application_no} />
              <DetailTile label="Registration No" value={selected.registration_no} />
              <DetailTile label="Student Name" value={selected.student_name} />
              <DetailTile label="Study Centre" value={selected.study_centre_name} />
              <DetailTile label="State" value={selected.state_name} />
              <DetailTile label="District" value={selected.district_name} />
              <DetailTile label="Phone" value={selected.phone} />
              <DetailTile label="Email" value={selected.email} />
              <DetailTile label="DOB" value={selected.dob} />
              <DetailTile label="Guardian Name" value={selected.guardian_name} />
              <DetailTile label="Session" value={selected.session_year} />
              <DetailTile label="Course" value={selected.course_name} />
              <DetailTile label="Status" value={selected.status} />
            </div>

            <div className="mt-3 p-3 rounded-4 border bg-light">
              <div className="text-muted small fw-bold mb-2">Address</div>
              <div>{selected.address || "-"}</div>
            </div>

            <div className="row g-3 mt-1">
              <div className="col-md-6"><DocumentCard label="Photo" fileName={selected.photo} isImage /></div>
              <div className="col-md-6"><DocumentCard label="Signature" fileName={selected.signature} isImage /></div>
              <div className="col-md-6"><DocumentCard label="ID Proof" fileName={selected.id_proof} /></div>
              <div className="col-md-6"><DocumentCard label="Academic Document" fileName={selected.academic_document} /></div>
            </div>
          </div>
        </div>
      ) : null}
    </DashboardLayout>
  );
}

export default ExamCompletedStudents;



